import { ExternalLink, Video } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ThumbnailWithFallback } from "@/components/shared/thumbnail-with-fallback"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type { GalleryRow } from "@/types/content-gallery"

/**
 * OKS-39 — Single gallery tile.
 *
 * Layout (UX Spec §3.2):
 *   - 9:16 portrait aspect (matches TikTok native).
 *   - The tile image fills the tile (`object-cover`) via the OKS-823
 *     fallback chain: safe TikTok thumbnail → app-owned cover → branded
 *     gradient + `Video` icon. The gradient shows only when BOTH the
 *     KD-17-filtered `tiktok_video_thumbnail_url` and `cover_url` are
 *     null/unloadable. See `imageOrFallback` for the nesting.
 *   - Top-left mission badge (visual; not nested anchor).
 *   - Bottom-left creator handle on a dark gradient strip.
 *   - Bottom-right `ExternalLink` icon hint.
 *
 * Click target = whole tile via outer `<a target="_blank" rel="noopener">`.
 * KD-17 escape hatch: when `tiktok_video_url` is null (URL hardening
 * filtered the stored value), the tile renders as a non-interactive
 * `<div>` — but the image inside still runs the full fallback chain
 * (OKS-823), so an attached cover renders even on an unsafe post URL.
 * UX Spec §3.2 broken-thumbnail branch widens to "broken URL".
 *
 * Accessibility (UX Spec §8):
 *   - `aria-label="Open TikTok post by {handle} for {mission}"` on the
 *     `<a>`. When non-interactive, the `<div>` carries the same label
 *     via `aria-label` for completeness.
 *   - `<img alt={t("content_gallery.tile.alt", ...)}>` interpolates the
 *     handle + mission title.
 *   - Mission badge + handle strip are visual-only; the `<a>`'s
 *     aria-label carries the full semantics.
 */
interface GalleryTileProps {
  row: GalleryRow
  className?: string
}

export function GalleryTile({ row, className }: GalleryTileProps) {
  const { t } = useTranslation()
  const initials = useInitials()

  const handle = row.creator.handle
    ? `@${row.creator.handle}`
    : t("content_gallery.tile.missing_handle")

  const ariaLabel = t("content_gallery.tile.open_aria", {
    handle,
    mission: row.mission.title,
  })
  const altText = t("content_gallery.tile.alt", {
    handle,
    mission: row.mission.title,
  })
  const avatarAlt = t("content_gallery.tile.avatar_alt", { handle })

  const tileClasses = cn(
    "group bg-muted relative block aspect-[9/16] overflow-hidden rounded-lg shadow-sm",
    "ring-border/40 hover:ring-primary ring-1 hover:ring-2",
    "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
    "transition-[box-shadow,ring-color] duration-150",
    className,
  )

  const overlay = (
    <>
      {/* Top-left mission badge */}
      <div className="absolute top-2 left-2 z-10">
        <Badge
          variant="secondary"
          className="bg-background/80 max-w-[12rem] truncate text-xs backdrop-blur-sm"
          title={row.mission.title}
          data-testid="gallery-tile-mission-badge"
        >
          {row.mission.title}
        </Badge>
      </div>

      {/* Bottom-left creator handle strip */}
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 py-2 text-xs text-white">
        <div className="flex min-w-0 items-center gap-1.5">
          {/* OKS-715 — creator TikTok avatar (app-owned AS mirror). Shrinks
              never; on null/404 shadcn collapses to initials — never a
              broken image. Mirrors crew-roster-cell.tsx. */}
          <Avatar
            className="size-5 shrink-0 ring-1 ring-white/30"
            data-testid="gallery-tile-avatar"
          >
            {row.creator.avatar_url && (
              <AvatarImage src={row.creator.avatar_url} alt={avatarAlt} />
            )}
            <AvatarFallback className="bg-white/20 text-[9px] font-semibold text-white">
              {initials(row.creator.name)}
            </AvatarFallback>
          </Avatar>
          <span
            className="min-w-0 truncate font-medium"
            data-testid="gallery-tile-handle"
          >
            {handle}
          </span>
        </div>
        <ExternalLink aria-hidden className="size-3.5 shrink-0 opacity-80" />
      </div>
    </>
  )

  // OKS-823 — tile image fallback chain: safe TikTok thumbnail → app-owned
  // cover → branded gradient. Expressed by NESTING `ThumbnailWithFallback`
  // (the shared primitive is deliberately UNCHANGED — its single-src/
  // single-fallback contract is also consumed by the submission-review
  // Sheet, so nesting keeps that surface at zero blast radius).
  //
  //   outer src = KD-17-filtered thumbnail (null when absent OR unsafe —
  //     the FE can't tell the two apart, which is the intended behavior:
  //     a nulled unsafe thumbnail falls through to the trusted cover).
  //   outer fallback = inner ThumbnailWithFallback(src = app-owned cover).
  //   inner fallback = <FallbackThumbnail/> (branded gradient, the floor).
  //
  // Consequences (all per PRD/UX Spec):
  //   - safe thumbnail present & loads → thumbnail wins; cover unused.
  //   - thumbnail null/unsafe, cover present → cover renders.
  //   - a cover in-browser LOAD ERROR → branded gradient (NOT back to the
  //     thumbnail): once the outer branch falls through, the inner's only
  //     fallback is the gradient. Matches the single-fallback primitive.
  //   - neither → branded gradient (unchanged OKS-39/506 designed state).
  // OKS-506 error semantics still hold on each level: a null src OR an
  // `onError` collapses that level to its own fallback.
  const imageOrFallback = (
    <ThumbnailWithFallback
      src={row.tiktok_video_thumbnail_url}
      alt={altText}
      loading="lazy"
      className="absolute inset-0 size-full object-cover"
      fallback={
        <ThumbnailWithFallback
          src={row.cover_url}
          alt={altText}
          loading="lazy"
          className="absolute inset-0 size-full object-cover"
          fallback={<FallbackThumbnail />}
        />
      }
    />
  )

  // KD-17 escape — `tiktok_video_url` filtered to null (non-https or
  // off-tiktok). Render the tile as a non-interactive div so we still
  // surface the row but never produce a clickable link to a hostile URL.
  //
  // OKS-823 — the IMAGE inside still follows the full fallback chain
  // (`imageOrFallback`), NOT a bare gradient: an unsafe *post* URL is
  // independent of whether we have a good thumbnail/cover to show, and an
  // app-owned cover is trusted regardless of the post URL. So a submission
  // with a broken post URL but an attached cover now renders the cover here
  // where it previously always showed the placeholder (UX Spec §3 edge
  // case). Clickability is unchanged — it still depends solely on the safe
  // post URL, never on which image paints.
  if (row.tiktok_video_url == null) {
    return (
      <div
        className={cn(tileClasses, "cursor-not-allowed opacity-90")}
        aria-label={ariaLabel}
        data-testid="gallery-tile-unsafe"
      >
        {imageOrFallback}
        {overlay}
      </div>
    )
  }

  return (
    <a
      href={row.tiktok_video_url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className={tileClasses}
      data-testid="gallery-tile"
    >
      {imageOrFallback}
      {overlay}
    </a>
  )
}

function FallbackThumbnail() {
  // `gallery-tile-fallback` marks the fallback visual. NOTE it is shown in
  // THREE cases — null cover, errored cover (OKS-506 onError), and the KD-17
  // unsafe-URL branch — so it is NOT error-specific. Disambiguate via the
  // wrapper: clickable `gallery-tile` <a> = null/error; `gallery-tile-unsafe`
  // <div> = KD-17.
  return (
    <div
      data-testid="gallery-tile-fallback"
      className="from-muted to-muted-foreground/30 text-muted-foreground absolute inset-0 flex items-center justify-center bg-gradient-to-br"
    >
      <Video aria-hidden className="size-10 opacity-70" />
    </div>
  )
}
